import { useState, useEffect, useMemo } from "react";
import {
  importTradesFromCSV,
  getCSVTemplateURL,
  getAccountsByUser,
  getAccountGroupsByUser,
  useQuery,
  useAction,
} from "wasp/client/operations";
import { Download } from "lucide-react";
import { Button } from "../client/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import { Input } from "../client/components/ui/input";
import { Label } from "../client/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../client/components/ui/select";
import { toast } from "../client/hooks/use-toast";
import useTranslation from "../client/hooks/useTranslation";
import { parseCSVFile, type ParsedTrade } from "./csvImport";
import { summarizeCsvTrades, applyCommissionRate } from "./csvPreview";
import { resolveCommissionForSymbol } from "./symbolCommissions";
import { useAccountBlewUpModal } from "../trading-accounts/components/useAccountBlewUpModal";
import { AccountBlewUpModal } from "../trading-accounts/components/AccountBlewUpModal";
import { useAutoDetectSessionToast } from "./useAutoDetectSessionToast";

interface CsvImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported?: () => void;
}

/**
 * Modale d'import CSV (choix du compte / groupe cible, taux de commission,
 * aperçu, erreurs de parsing), extraite de TradingJournalPage pour être
 * réutilisable. Les queries sont identiques à celles du journal : le cache
 * react-query est partagé.
 */
export default function CsvImportDialog({ open, onOpenChange, onImported }: CsvImportDialogProps) {
  const t = useTranslation();
  const importTradesFn = useAction(importTradesFromCSV);
  const { handleViolations, blewUpModalProps } = useAccountBlewUpModal();
  const handleAutoDetectResult = useAutoDetectSessionToast();

  const { isLoading: isLoadingTemplate, refetch: refetchTemplateURL } = useQuery(getCSVTemplateURL);
  const { data: accountsData } = useQuery(getAccountsByUser);
  const accounts = (accountsData as any)?.accounts ?? [];
  const accountsList = Array.isArray(accounts) ? accounts : [];
  const { data: accountGroups = [] } = useQuery(getAccountGroupsByUser);

  const [isImportErrorsDialogOpen, setIsImportErrorsDialogOpen] = useState(false);
  const [importErrors, setImportErrors] = useState<Array<{ row: number; error: string }>>([]);

  // ── CSV import target (account or group) ────────────────────────────────────
  // The CSV formats we parse (standard template, Quantower, TopstepX) carry no
  // account identity, so the trader has to say where the rows land. Without this
  // the import silently wrote accountId: null — the trades existed but showed up
  // in no account balance, no prop firm metric and inherited no strategy.
  // Value shape mirrors AddTradeModal: "none" | "account:<id>" | "group:<id>".
  const [importAccountSelection, setImportAccountSelection] = useState<string>("none");
  // The CSV is staged, never imported on pick: the trader must confirm so the
  // account choice above is actually taken into account (picking the file first
  // used to fire the import straight away, landing the rows on no account).
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  // Aperçu du fichier choisi, comme dans la modale Tradovate : l'utilisateur
  // doit voir ce qu'il s'apprête à importer avant de valider.
  const [importPreview, setImportPreview] = useState<ParsedTrade[] | null>(null);
  const [importPreviewError, setImportPreviewError] = useState<string | null>(null);
  const [importCommissionPerTrade, setImportCommissionPerTrade] = useState<string>(
    () => localStorage.getItem("tradovate_commission_per_trade") ?? "0",
  );
  // L'utilisateur peut corriger le taux proposé : on cesse alors de l'écraser
  // à chaque changement de compte, sinon sa saisie disparaîtrait sous ses yeux.
  const [importCommissionTouched, setImportCommissionTouched] = useState(false);

  const isImportAccountActive = (a: any) => a.status !== "inactive" && a.isActive !== false;
  const importAccountsList = accountsList.filter(isImportAccountActive);
  const importGroupsList = (accountGroups as any[]).filter((g: any) =>
    accountsList.some((a: any) => a.groupId === g.id && isImportAccountActive(a)),
  );

  /** Accounts the import writes to: 1 for an account, N for a group, [null] for none. */
  const getImportTargetAccountIds = (): (string | null)[] => {
    if (importAccountSelection.startsWith("account:")) {
      return [importAccountSelection.slice(8)];
    }
    if (importAccountSelection.startsWith("group:")) {
      const groupId = importAccountSelection.slice(6);
      const groupAccounts = importAccountsList.filter((a: any) => a.groupId === groupId);
      return groupAccounts.length > 0 ? groupAccounts.map((a: any) => a.id) : [null];
    }
    return [null];
  };

  /**
   * Template de prop firm du compte ou du groupe choisi, source des taux de
   * commission. Même résolution que la modale Tradovate : le template propre au
   * compte prime, puis celui de son groupe.
   */
  const importResolvedTemplate = useMemo(() => {
    if (importAccountSelection.startsWith("account:")) {
      const account = importAccountsList.find(
        (a: any) => a.id === importAccountSelection.slice(8),
      );
      const firm =
        account?.effectiveTemplate ??
        account?.propFirmTemplate ??
        account?.group?.propFirmTemplate ??
        null;
      // Fusion firm × palier VIVANT faite côté serveur (effectiveCommissions,
      // référence stable du cache de query). Repli sur la chaîne firm si le
      // compte n'en porte pas (ex. compte hors prop firm).
      return account?.effectiveCommissions ?? firm;
    }
    if (importAccountSelection.startsWith("group:")) {
      const group = importGroupsList.find(
        (g: any) => g.id === importAccountSelection.slice(6),
      );
      return group?.propFirmTemplate ?? null;
    }
    return null;
  }, [importAccountSelection, importAccountsList, importGroupsList]);

  // Changer de compte doit proposer le taux de SA prop firm : sans cela,
  // l'utilisateur devait connaître puis saisir le tarif à la main.
  useEffect(() => {
    if (importCommissionTouched) return;
    const rate = importResolvedTemplate?.commissionPerContract;
    setImportCommissionPerTrade(rate != null ? String(Math.abs(rate)) : "0");
  }, [importResolvedTemplate, importCommissionTouched]);

  const handleDownloadTemplate = async () => {
    try {
      const result = await refetchTemplateURL();
      if (result.data) {
        window.open(result.data, "_blank");
        toast({
          title: t.journal.templateDownloaded,
          description: t.journal.templateDownloadedDescription,
        });
      }
    } catch (error: any) {
      toast({
        title: t.common.error,
        description: error.message || t.journal.cannotDownloadTemplate,
        variant: "destructive",
      });
    }
  };

  /** Closes the import modal and drops the staged file + account choice. */
  const closeImportDialog = () => {
    onOpenChange(false);
    setImportFile(null);
    setImportAccountSelection("none");
  };

  /**
   * Parse le fichier dès sa sélection pour alimenter l'aperçu. L'import réel
   * n'a lieu qu'à la confirmation : parser deux fois est sans effet de bord et
   * évite de faire découvrir le contenu du fichier après coup.
   */
  const handleImportFileSelected = async (file: File | null) => {
    setImportFile(file);
    setImportPreview(null);
    setImportPreviewError(null);
    if (!file) return;

    try {
      const result = await parseCSVFile(file);
      if (result.errors.length > 0) {
        setImportPreviewError(result.errors[0].error);
        return;
      }
      if (result.valid.length === 0) {
        setImportPreviewError(t.journal.noValidTradesDescription);
        return;
      }
      setImportPreview(result.valid);
    } catch {
      setImportPreviewError(t.journal.noValidTradesDescription);
    }
  };

  const handleImport = async (file: File) => {
    setIsImporting(true);
    try {
      const result = await parseCSVFile(file);
      if (result.errors.length > 0) {
        setImportErrors(result.errors);
        setIsImportErrorsDialogOpen(true);
        toast({
          title: t.journal.importErrors,
          description: t.journal.importErrorsCount.replace("{count}", String(result.errors.length)),
          variant: "destructive",
        });
        return;
      }

      if (result.valid.length === 0) {
        toast({
          title: t.journal.noValidTrades,
          description: t.journal.noValidTradesDescription,
          variant: "destructive",
        });
        return;
      }

      // Les exports qui ne détaillent pas leurs frais (Tradovate) arrivaient
      // sans commission : un fichier terminé à -60 entrait à 0 $. Le taux saisi
      // leur est appliqué, comme le fait déjà la modale Tradovate.
      const rate = importCommissionPerTrade !== ""
        ? parseFloat(importCommissionPerTrade) || 0
        : 0;
      const withFees = applyCommissionRate(result.valid, rate, (symbol) =>
        resolveCommissionForSymbol(symbol, importResolvedTemplate),
      );

      // Stamp every row with the chosen target. A group selection imports the
      // same rows once per account in the group, matching AddTradeModal.
      const targetIds = getImportTargetAccountIds();
      let totalImported = 0;
      let totalDuplicates = 0;
      for (const accountId of targetIds) {
        const trades = withFees.map((tr) => ({ ...tr, accountId }));
        const importResult = await importTradesFn({ trades });
        totalImported += importResult.imported;
        totalDuplicates += importResult.duplicates ?? 0;
        handleViolations((importResult as any)?.accountViolations);
        handleAutoDetectResult((importResult as any)?.autoDetectResult);
      }
      const successDescription = t.journal.importSuccessDescription.replace("{count}", String(totalImported));
      toast({
        title: totalImported === 0 && totalDuplicates > 0
          ? t.journal.importNothingNew
          : t.journal.importSuccess,
        description: totalDuplicates > 0
          ? `${successDescription} ${t.journal.importDuplicatesSkipped.replace("{count}", String(totalDuplicates))}`
          : successDescription,
      });
      closeImportDialog();
      onImported?.();
    } catch (error: any) {
      toast({
        title: t.common.error,
        description: error.message || t.journal.cannotImport,
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <>
      {/* Import Dialog */}
      <Dialog
        open={open}
        onOpenChange={(next) => (next ? onOpenChange(true) : closeImportDialog())}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.journal.importTrades} (CSV)</DialogTitle>
            <DialogDescription>{t.journal.selectFile}</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Button
                type="button"
                variant="outline"
                onClick={handleDownloadTemplate}
                className="w-full mb-4"
                disabled={isLoadingTemplate}
              >
                <Download className="mr-2 h-4 w-4" />
                {isLoadingTemplate ? t.common.loading : t.journal.downloadTemplate}
              </Button>
            </div>
            {/* Target account comes BEFORE the file picker: the import only runs
                on explicit confirmation, but the order keeps the account choice
                in front of the trader rather than after the file is picked. */}
            <div className="space-y-1.5">
              <Label>{t.journal.selectAccountOrGroup}</Label>
              <Select value={importAccountSelection} onValueChange={setImportAccountSelection}>
                <SelectTrigger>
                  <SelectValue placeholder={t.journal.selectAccountOrGroupPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t.journal.noAccount}</SelectItem>
                  {importGroupsList.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                        {t.journal.groupPrefix}
                      </div>
                      {importGroupsList.map((group: any) => (
                        <SelectItem key={`group:${group.id}`} value={`group:${group.id}`}>
                          {group.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {importAccountsList.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                        {t.journal.account}
                      </div>
                      {importAccountsList.map((account: any) => (
                        <SelectItem key={`account:${account.id}`} value={`account:${account.id}`}>
                          {account.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
              {importAccountSelection === "none" && (
                <p className="text-xs text-muted-foreground">{t.journal.importNoAccountHint}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="csv-commission">{t.journal.importCommissionLabel}</Label>
              <Input
                id="csv-commission"
                type="number"
                step="0.01"
                min="0"
                value={importCommissionPerTrade}
                onChange={(e) => {
                  setImportCommissionTouched(true);
                  setImportCommissionPerTrade(e.target.value);
                }}
                disabled={isImporting}
              />
              <p className="text-xs text-muted-foreground">{t.journal.importCommissionHint}</p>
            </div>
            <div>
              <Label htmlFor="csv-file">{t.journal.selectFile}</Label>
              <Input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={(e) => {
                  void handleImportFileSelected(e.target.files?.[0] ?? null);
                }}
                className="mt-2"
                disabled={isImporting}
              />
              {importFile && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {t.journal.importSelectedFile.replace("{name}", importFile.name)}
                </p>
              )}
            </div>

            {/* Aperçu : mêmes chiffres que la modale Tradovate, commissions
                comprises, pour que l'utilisateur valide en connaissance de cause. */}
            {importPreview && importPreview.length > 0 && (() => {
              const rate = importCommissionPerTrade !== ""
                ? parseFloat(importCommissionPerTrade) || 0
                : 0;
              const summary = summarizeCsvTrades(
                applyCommissionRate(importPreview, rate, (symbol) =>
                  resolveCommissionForSymbol(symbol, importResolvedTemplate),
                ),
              );
              const multiplier = getImportTargetAccountIds().length;
              return (
                <div className="rounded-md border border-emerald-600/40 bg-emerald-950/20 p-3 space-y-2">
                  <p className="text-sm font-medium text-emerald-500">
                    {summary.count} {t.session.tradovateParseSuccess}
                  </p>
                  <div className="flex items-center gap-4 text-xs">
                    <span>
                      <span className="font-semibold text-green-500">{summary.longs}</span> {t.journal.longsLabel}
                    </span>
                    <span>
                      <span className="font-semibold text-red-500">{summary.shorts}</span> {t.journal.shortsLabel}
                    </span>
                    <span>
                      <span
                        className={`font-semibold ${summary.totalPnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"}`}
                      >
                        {summary.totalPnl >= 0 ? "+" : ""}
                        {summary.totalPnl.toFixed(2)}
                      </span>{" "}
                      P&L
                    </span>
                  </div>
                  {multiplier > 1 && (
                    <p className="border-t border-emerald-600/30 pt-2 text-xs text-muted-foreground">
                      {t.journal.importGroupMultiplier
                        .replace("{count}", String(multiplier))
                        .replace("{total}", String(summary.count * multiplier))}
                    </p>
                  )}
                </div>
              );
            })()}

            {importPreviewError && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3">
                <p className="text-xs text-destructive">{importPreviewError}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeImportDialog} disabled={isImporting}>
              {t.common.cancel}
            </Button>
            <Button
              onClick={() => importFile && handleImport(importFile)}
              disabled={!importFile || isImporting}
            >
              {isImporting ? t.journal.importInProgress : t.journal.importConfirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Errors Dialog */}
      <Dialog open={isImportErrorsDialogOpen} onOpenChange={setIsImportErrorsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t.journal.importErrors}</DialogTitle>
            <DialogDescription>
              {t.journal.importErrorsCount.replace("{count}", String(importErrors.length))}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2">
              {importErrors.map((error, index) => (
                <div
                  key={index}
                  className="p-3 bg-destructive/10 border border-destructive/20 rounded-md"
                >
                  <div className="flex items-start gap-2">
                    <span className="font-semibold text-destructive">{error.row}:</span>
                    <span className="text-sm text-foreground">{error.error}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsImportErrorsDialogOpen(false);
                setImportErrors([]);
              }}
            >
              {t.common.close}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AccountBlewUpModal {...blewUpModalProps} />
    </>
  );
}
